import { errorCodeOf, errorDetailsOf } from "@luckymenu/api-client";
import { useTRPCClient } from "@luckymenu/api-client/react";
import {
	FILE_RULES,
	type FileInfo,
	type FilePurpose,
	UploadError,
	type UploadErrorCode,
	type UploadMime,
} from "@luckymenu/core";
import { useCallback, useEffect, useRef, useState } from "react";
import type { PickedFile } from "../adapters";
import { usePlatform } from "../provider";

export type FileUploadState =
	| { status: "idle" }
	| { status: "uploading"; progress: number; name: string }
	| { status: "done"; file: FileInfo }
	| { status: "error"; error: UploadErrorCode };

/** The upload error for an API refusal of the token or confirmation. */
function apiUploadError(error: unknown): UploadErrorCode {
	const code = errorCodeOf(error);
	if (code === "NETWORK") return "network";
	if (code === "FORBIDDEN") return "forbidden";
	if (code === "RATE_LIMITED") return "rate_limited";
	const reason = errorDetailsOf(error).reason;
	if (reason === "type" || reason === "size" || reason === "quota") return reason;
	return "failed";
}

/**
 * Uploads one file for `purpose` in the active restaurant: checks type and
 * size locally, gets a token (`staff.files.createUploadToken`), sends the
 * bytes through the platform adapter with progress, and confirms
 * (`staff.files.confirmUpload`). Both staff apps show `state`.
 */
export function useFileUpload(purpose: FilePurpose) {
	const platform = usePlatform();
	const client = useTRPCClient();
	const [state, setState] = useState<FileUploadState>({ status: "idle" });
	const abort = useRef<AbortController | null>(null);

	useEffect(() => () => abort.current?.abort(), []);

	const upload = useCallback(
		async (file: PickedFile): Promise<FileInfo | null> => {
			abort.current?.abort();
			const controller = new AbortController();
			abort.current = controller;
			const rule = FILE_RULES[purpose];
			const fail = (error: UploadErrorCode) => {
				if (!controller.signal.aborted || error === "cancelled")
					setState({ status: "error", error });
				return null;
			};
			if (!rule.mimes.includes(file.mime as UploadMime)) return fail("type");
			if (file.size > rule.maxBytes) return fail("size");

			setState({ status: "uploading", progress: 0, name: file.name });
			let ticket: Awaited<ReturnType<typeof client.staff.files.createUploadToken.mutate>>;
			try {
				ticket = await client.staff.files.createUploadToken.mutate({
					purpose,
					mime: file.mime as UploadMime,
					size: file.size,
				});
			} catch (error) {
				return fail(apiUploadError(error));
			}
			try {
				await platform.files.transfer(
					file,
					ticket,
					(progress) => {
						if (!controller.signal.aborted)
							setState({ status: "uploading", progress, name: file.name });
					},
					controller.signal,
				);
			} catch (error) {
				return fail(error instanceof UploadError ? error.code : "network");
			}
			try {
				const info = await client.staff.files.confirmUpload.mutate({ fileId: ticket.fileId });
				if (!controller.signal.aborted) setState({ status: "done", file: info });
				return info;
			} catch (error) {
				return fail(apiUploadError(error));
			}
		},
		[client, platform, purpose],
	);

	/** Opens the picker, then uploads. Does nothing if the person cancels. */
	const pickAndUpload = useCallback(async (): Promise<FileInfo | null> => {
		const file = await platform.files.pickImage();
		return file ? upload(file) : null;
	}, [platform, upload]);

	const cancel = useCallback(() => {
		abort.current?.abort();
		setState({ status: "error", error: "cancelled" });
	}, []);

	const reset = useCallback(() => {
		abort.current?.abort();
		setState({ status: "idle" });
	}, []);

	return { state, upload, pickAndUpload, cancel, reset };
}
